// Плагин таблиц СТО:
//  - инварианты: головка есть всегда (4.9.8) и это ведущие строки; ячеек-«головок» в строках данных нет;
//    ширины граф (colwidth, пиксели) всегда в сумме равны ширине поля набора 17 см (таблица = поле набора, как в DOCX);
//  - декорации: выравнивание по умолчанию как в writer (боковик влево, значения по центру, длинный текст влево),
//    прочерк на месте пустой ячейки (экспорт ставит «–», 4.9.21), подсветка головки, авто-ширина граф;
//  - изменение ширины граф перетаскиванием границы (как в Word: соседние графы меняются, таблица - нет),
//    с подсказкой в сантиметрах; двойной щелчок по границе - ширины по содержимому.
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';
import { NUMERIC_RE, applyWidths, cells, currentWidths, headerRowCount, normalizeWidths, tableCtxAt, type TableCtx } from './model';
import { PX_PER_CM, TEXT_W_PX } from '../util';

export const tfcTableKey = new PluginKey('tfcTables');

// ------------------------------------------------------------------------------------------------ инварианты
function stoTables(doc: PMNode): number[] {
  const out: number[] = [];
  doc.descendants((n, pos) => {
    if (n.type.name === 'stoTable') { out.push(pos); return false; }
    return n.type.name === 'doc' || n.type.name === 'appendix';
  });
  return out;
}

function fixTable(tr: Transaction, t: TableCtx): void {
  const schema = tr.doc.type.schema;
  const H = headerRowCount(t.table);
  for (const c of cells(t)) {
    const want = (H === 0 ? c.row === 0 : c.row < H) ? schema.nodes.tableHeader : schema.nodes.tableCell;
    if (c.node.type !== want) tr.setNodeMarkup(c.pos, want, want === schema.nodes.tableHeader ? { ...c.node.attrs, align: null } : c.node.attrs);
  }
  const ws = currentWidths(t);
  if (ws.some((x) => x)) {
    const sum = ws.reduce<number>((a, b) => a + (b || 0), 0);
    if (ws.some((x) => !x) || Math.abs(sum - TEXT_W_PX) > 3) applyWidths(tr, tableCtxAt(tr.doc, t.stoPos)!, normalizeWidths(ws));
  }
}

function appendFix(state: EditorState): Transaction | null {
  const tr = state.tr;
  for (const pos of stoTables(state.doc)) {
    const t = tableCtxAt(tr.doc, pos);
    if (t) fixTable(tr, t);
  }
  return tr.docChanged ? tr : null;
}

// ------------------------------------------------------------------------------------------------ декорации
export type ColAlign = 'left' | 'center' | 'right';

/** Выравнивание граф по умолчанию - как writer/builder.emit_table (4.9.9, 4.9.23). */
export function defaultAligns(t: TableCtx): ColAlign[] {
  const out: ColAlign[] = [];
  const all = cells(t);
  for (let c = 0; c < t.map.width; c++) {
    const vals = all.filter((x) => x.col === c && x.row >= t.headerRows).map((x) => x.node.textContent.replace(/[`*$]/g, '').trim())
      .filter((v) => v && !['-', '–', '—'].includes(v));
    const numeric = vals.every((v) => NUMERIC_RE.test(v));
    if (c === 0) out.push(vals.length && numeric ? 'center' : 'left');
    else {
      const longtxt = vals.length > 0 && !numeric && vals.reduce((a, v) => a + v.length, 0) / vals.length > 40;
      out.push(longtxt ? 'left' : 'center');
    }
  }
  return out;
}

function decorate(doc: PMNode): DecorationSet {
  const decos: Decoration[] = [];
  for (const pos of stoTables(doc)) {
    const t = tableCtxAt(doc, pos);
    if (!t) continue;
    const def = defaultAligns(t);
    const ws = currentWidths(t);
    const cls = ['tfc-table'];
    if (!ws.some((x) => x)) cls.push('tfc-autow');
    if (t.map.height > 15) cls.push('tfc-long');
    decos.push(Decoration.node(t.stoPos, t.stoPos + t.sto.nodeSize, { class: cls.join(' ') }));
    for (const c of cells(t)) {
      const end = c.pos + c.node.nodeSize;
      if (c.row < t.headerRows) { decos.push(Decoration.node(c.pos, end, { class: 'tfc-head' })); continue; }
      const txt = c.node.textContent.trim();
      const empty = !txt && c.node.childCount === 1 && c.node.firstChild!.childCount === 0;
      let al: string = c.node.attrs.align || def[c.col] || 'left';
      const k: string[] = [];
      if (/^[-–—]$/.test(txt)) al = 'center';
      if (empty && c.col > 0) { al = 'center'; k.push('tfc-empty'); }
      if (!c.node.attrs.align) k.push('tfc-auto');
      k.push('tfc-al-' + al);
      decos.push(Decoration.node(c.pos, end, { class: k.join(' ') }));
    }
  }
  return DecorationSet.create(doc, decos);
}

// ------------------------------------------------------------------------------------------------ ширины граф мышью
const HANDLE = 5;
const MIN_W = 28;          // ≈ 0,75 см
interface Hover { stoPos: number; boundary: number; tableEl: HTMLTableElement }
let hover: Hover | null = null;
let guide: HTMLDivElement | null = null;
let tip: HTMLDivElement | null = null;
let dragging = false;

function ensureGuide() {
  if (!guide) {
    guide = document.createElement('div');
    guide.className = 'tfc-col-guide';
    document.body.appendChild(guide);
    tip = document.createElement('div');
    tip.className = 'tfc-col-tip';
    document.body.appendChild(tip);
  }
}
function hideGuide() {
  if (guide) guide.style.display = 'none';
  if (tip) tip.style.display = 'none';
}
function showGuide(x: number, r: DOMRect, text?: string) {
  ensureGuide();
  Object.assign(guide!.style, { display: 'block', left: `${x - 1}px`, top: `${r.top}px`, height: `${r.height}px` });
  if (text) { Object.assign(tip!.style, { display: 'block', left: `${x}px`, top: `${r.top - 26}px` }); tip!.textContent = text; }
  else tip!.style.display = 'none';
}

/** Экранные границы граф: edges[0..width]. */
function columnEdges(view: EditorView, t: TableCtx, tableEl: HTMLTableElement): number[] {
  const tr = tableEl.getBoundingClientRect();
  const edges: (number | null)[] = Array(t.map.width + 1).fill(null);
  edges[0] = tr.left; edges[t.map.width] = tr.right;
  for (const c of cells(t)) {
    const dom = view.nodeDOM(c.pos) as HTMLElement | null;
    if (!dom || !dom.getBoundingClientRect) continue;
    const r = dom.getBoundingClientRect();
    const span = c.node.attrs.colspan || 1;
    if (edges[c.col] === null) edges[c.col] = r.left;
    if (edges[c.col + span] === null) edges[c.col + span] = r.right;
  }
  // пропуски - интерполяцией
  for (let i = 1; i < edges.length; i++) {
    if (edges[i] !== null) continue;
    let j = i; while (edges[j] === null) j++;
    const a = edges[i - 1]!; const b = edges[j]!;
    for (let k = i; k < j; k++) edges[k] = a + ((b - a) * (k - i + 1)) / (j - i + 1);
  }
  return edges as number[];
}

function hitBoundary(view: EditorView, e: MouseEvent): Hover | null {
  const target = e.target as HTMLElement | null;
  const cell = target?.closest?.('td, th') as HTMLElement | null;
  if (!cell || !view.dom.contains(cell) || !cell.closest('.sto-table')) return null;
  const r = cell.getBoundingClientRect();
  const side = e.clientX - r.left <= HANDLE ? 'l' : r.right - e.clientX <= HANDLE ? 'r' : null;
  if (!side) return null;
  let pos: number;
  try { pos = view.posAtDOM(cell, 0); } catch { return null; }
  const $p = view.state.doc.resolve(pos);
  for (let d = $p.depth; d > 2; d--) {
    const n = $p.node(d);
    if (n.type.name === 'tableCell' || n.type.name === 'tableHeader') {
      let sd = d - 1;
      while (sd > 0 && $p.node(sd).type.name !== 'stoTable') sd--;
      if (sd <= 0) return null;
      const stoPos = $p.before(sd);
      const t = tableCtxAt(view.state.doc, stoPos);
      if (!t) return null;
      const rect = t.map.findCell($p.before(d) - t.tableStart);
      const boundary = side === 'r' ? rect.right : rect.left;
      if (boundary <= 0 || boundary >= t.map.width) return null;
      return { stoPos, boundary, tableEl: cell.closest('table') as HTMLTableElement };
    }
  }
  return null;
}

function cmText(px: number) {
  return `${(Math.round((px / PX_PER_CM) * 10) / 10).toLocaleString('ru-RU')} см`;
}

function startDrag(view: EditorView, h: Hover, e: MouseEvent) {
  const t = tableCtxAt(view.state.doc, h.stoPos);
  if (!t) return;
  const edges = columnEdges(view, t, h.tableEl);
  const screen = edges.slice(1).map((x, i) => x - edges[i]);
  const cur = currentWidths(t);
  const w0 = cur.every((x) => x) ? normalizeWidths(cur) : normalizeWidths(screen);
  const scale = TEXT_W_PX / (edges[edges.length - 1] - edges[0]);
  const b = h.boundary;
  const pair = w0[b - 1] + w0[b];
  const cols = Array.from(h.tableEl.querySelectorAll(':scope > colgroup > col')) as HTMLElement[];
  const startX = e.clientX;
  const orig = cols.map((c) => c.style.width);
  const origW = h.tableEl.style.width;
  let w = [...w0];
  let moved = false;
  dragging = true;
  h.tableEl.classList.add('tfc-resizing');
  h.tableEl.style.width = `${TEXT_W_PX}px`;
  const apply = () => { cols.forEach((c, i) => { if (w[i]) c.style.width = `${w[i]}px`; }); };
  apply();
  const move = (ev: MouseEvent) => {
    if (Math.abs(ev.clientX - startX) > 2) moved = true;
    let left = w0[b - 1] + (ev.clientX - startX) * scale;
    // привязка к целым миллиметрам; Alt - без привязки
    if (!ev.altKey) left = Math.round(left / (PX_PER_CM / 10)) * (PX_PER_CM / 10);
    left = Math.max(MIN_W, Math.min(pair - MIN_W, left));
    w = [...w0]; w[b - 1] = left; w[b] = pair - left;
    apply();
    const r = h.tableEl.getBoundingClientRect();
    const x = r.left + (w.slice(0, b).reduce((a, v) => a + v, 0) / TEXT_W_PX) * r.width;
    showGuide(x, r, `${cmText(w[b - 1])} | ${cmText(w[b])}`);
  };
  const up = () => {
    window.removeEventListener('mousemove', move, true);
    window.removeEventListener('mouseup', up, true);
    dragging = false;
    h.tableEl.classList.remove('tfc-resizing');
    hideGuide();
    const t2 = tableCtxAt(view.state.doc, h.stoPos);
    if (!moved || !t2 || t2.map.width !== w.length) {
      cols.forEach((c, i) => { c.style.width = orig[i]; });
      h.tableEl.style.width = origW;
      return;
    }
    const tr = view.state.tr;
    applyWidths(tr, t2, normalizeWidths(w));
    if (tr.docChanged) view.dispatch(tr);
  };
  window.addEventListener('mousemove', move, true);
  window.addEventListener('mouseup', up, true);
  move(e);
}

// ------------------------------------------------------------------------------------------------ расширение
export const TfcTablesExtension = Extension.create({
  name: 'tfcTables',
  priority: 1100,     // раньше columnResizing из prosemirror-tables: границы граф обрабатываем сами
  addProseMirrorPlugins() {
    return [new Plugin({
      key: tfcTableKey,
      state: {
        init: (_, state) => decorate(state.doc),
        apply: (tr, prev, _o, state) => (tr.docChanged ? decorate(state.doc) : prev),
      },
      appendTransaction: (trs, _old, state) => (trs.some((t) => t.docChanged) ? appendFix(state) : null),
      props: {
        decorations: (state) => tfcTableKey.getState(state),
        handleDOMEvents: {
          mousemove: (view, e) => {
            if (dragging) return true;
            const inCell = !!(e.target as HTMLElement)?.closest?.('.sto-table td, .sto-table th');
            const h = view.editable ? hitBoundary(view, e) : null;
            hover = h;
            if (h) {
              const t = tableCtxAt(view.state.doc, h.stoPos)!;
              const edges = columnEdges(view, t, h.tableEl);
              showGuide(edges[h.boundary], h.tableEl.getBoundingClientRect());
              view.dom.classList.add('tfc-col-resize');
            } else {
              view.dom.classList.remove('tfc-col-resize');
              if (guide?.style.display === 'block') hideGuide();
            }
            return inCell;     // columnResizing prosemirror-tables не нужен: границы обрабатываются здесь
          },
          mouseleave: (view) => {
            if (!dragging) { hover = null; hideGuide(); view.dom.classList.remove('tfc-col-resize'); }
            return false;
          },
          mousedown: (view, e) => {
            if (e.button !== 0 || !hover) return false;
            const h = hitBoundary(view, e);
            if (!h) return false;
            e.preventDefault();
            if (e.detail >= 2) {
              // двойной щелчок - ширины по содержимому (как «Автоподбор» Word)
              const t = tableCtxAt(view.state.doc, h.stoPos);
              if (t) { const tr = view.state.tr; applyWidths(tr, t, null); view.dispatch(tr); }
              hideGuide();
              return true;
            }
            startDrag(view, h, e);
            return true;
          },
        },
      },
    })];
  },
});

export { TableMap };
